<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Payroll" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            @click="handleExportToExcel"
            :block="true"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <PayrollReportFilters
        class="px-5 mb-sm-5 pb-8"
      />
    </div>
    <SkippedAssignmentsWarning :skippedAssignments="skippedAssignments"></SkippedAssignmentsWarning>
    <v-row
      v-if="appliedPayFrequencyId && clientPayrollSummariesDataValue.length > 0"
      class="hide-on-print"
    >
      <v-col cols="12" class="pt-sm-0">
        <v-card class="mt-3 mt-md-0">
          <v-card-text>
            <v-row>
              <v-col cols="12" class="px-4">
                <span class="headline">
                  <small>
                    Proration Needed
                    <div class="yellow-square"></div>
                  </small>
                </span>
                <span class="headline ml-5">
                  <small>
                    Total Pay Edited
                    <div class="blue-square"></div>
                  </small>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <ReportHeader
      reportTitle="Payroll Report"
      :reportCriteria="reportFilterCriteria"
    ></ReportHeader>
    <v-row>
      <v-col cols="12">
        <NoDataAvailableMessage
          v-if="appliedPayFrequencyId && clientPayrollSummariesDataValue.length === 0"
          message="No data for this time period"
        ></NoDataAvailableMessage>
        <PayrollReportTable
          v-if="appliedPayFrequencyId && clientPayrollSummariesDataValue.length > 0"
          :clientPayrollSummaries="clientPayrollSummariesDataValue"
          :staffPayrollSummaries="payrollReport.staffPayrollSummaries"
          :businessEntityId="appliedFilters.businessEntityId"
          @OneOffFeeListModalClose="getPayrollReportWithFilters"
          @editedTotalPay="getPayrollReportWithFilters"
        ></PayrollReportTable>
      </v-col>
    </v-row>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { ButtonPrint, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import ReportHeader from '@/components/reports/ReportHeader'
import PayrollReportFilters from './PayrollReportFilters'
import PayrollReportTable from './PayrollReportTable'
import { SimplePageHeader } from '@/components/pageComponents'
import { mapFields } from '@/vueHelpers/vuex'
import { mapActions, mapGetters, mapState } from 'vuex'
import { formatCurrency, formatDate, getPayRateTypeLabel, getPayFrequencyLabel, formatDecimal } from '@/lib/display'
import { getPresentTypeSummaries, getPayrollTypeColumns, getTypeSummaryHours, getTypeSummaryAmount, TypeSummaryAmountField, isTypePresentOnRow, buildSkippedRow } from '@/lib/reports'
import excel from '@/lib/export/excel'
import { NoDataAvailableMessage, SkippedAssignmentsWarning } from '@/components/reports'
import { hasQueryChanged } from '@/lib/pagingAndSorting'
import {
  PAYROLL_REPORT_QUERY_KEYS,
  buildAppliedFilters,
  buildPayrollReportQuery,
  parsePayrollReportQuery,
  queryDescribesFilters
} from './payrollSearchPolicy'

export default {
  components: {
    ButtonPrint,
    BottomSpacer,
    ReportHeader,
    PayrollReportFilters,
    PayrollReportTable,
    SimplePageHeader,
    ButtonCustomIcon,
    NoDataAvailableMessage,
    SkippedAssignmentsWarning
  },
  data () {
    return {
      payrollSummariesEmpty: [],
      isInitialized: false
    }
  },
  computed: {
    ...mapFields('payrollReport', ['payrollReport']),
    ...mapFields('payrollReportFilters', [
      'payFrequencyId',
      'dateBegin',
      'dateEnd',
      'businessEntityId',
      'showDetails',
      'clientId',
      'useCustomRange'
    ]),
    // Everything the report shows reads the applied set, which is the filters the report on screen
    // was searched with. The mapped fields above are the inputs, and in Custom Range mode they run
    // ahead of the report until Search is pressed.
    ...mapState('payrollReportFilters', ['appliedFilters']),
    ...mapGetters('payrollReportFilters', ['pendingFilters']),
    appliedPayFrequencyId () {
      return this.appliedFilters.payFrequencyId
    },
    appliedPeriod () {
      const { dateBegin, dateEnd } = this.appliedFilters

      // Null rather than a half-empty range, so a report that has not been searched yet prints no
      // Period row at all instead of claiming one.
      return dateBegin && dateEnd ? formatDate(dateBegin) + ' - ' + formatDate(dateEnd) : null
    },
    clientPayrollSummariesDataValue: {
      get () {
        if (this.payrollReport &&
          this.payrollReport.clientPayrollSummaries &&
          this.payrollReport.clientPayrollSummaries.length > 0) {
          if (this.appliedFilters.clientId) {
            return this.payrollReport.clientPayrollSummaries.filter(row => row.clientId === this.appliedFilters.clientId)
          } else {
            return this.payrollReport.clientPayrollSummaries
          }
        }
        return this.payrollSummariesEmpty
      }
    },
    staffPayrollSummaries: {
      get () {
        if (this.payrollReport &&
          this.payrollReport.staffPayrollSummaries &&
          this.payrollReport.staffPayrollSummaries.length > 0) {
          return this.payrollReport.staffPayrollSummaries
        } else {
          return this.payrollSummariesEmpty
        }
      }
    },
    skippedAssignments: {
      get () {
        return (this.payrollReport && this.payrollReport.skippedAssignments) || []
      }
    },
    reportFilterCriteria: {
      get () {
        return [
          { 'key': 'Pay Frequency', 'value': getPayFrequencyLabel(this.appliedPayFrequencyId) },
          { 'key': 'Period', 'value': this.appliedPeriod }
        ]
      }
    },
    // What the URL describes: the search that produced the report, plus Show Details, which picks
    // columns rather than rows and so never waits on a search.
    reportQueryFilters () {
      return { ...this.appliedFilters, showDetails: this.showDetails }
    }
  },
  watch: {
    // A single watcher over everything the URL describes, so a batch of assignments (e.g.
    // created(), or a search that commits several filters at once) triggers one router sync
    // rather than one per field.
    reportQueryFilters: {
      handler: 'handleReportFilterChange',
      deep: true
    },
    '$route.query' (nextQuery) {
      // Filter changes update the URL first. Once the route watcher runs, the store filters and
      // route query already match, so skip reapplying the filters and fetching the report twice.
      if (!this.isInitialized || this.queryDescribesCurrentFilters(nextQuery)) {
        return
      }

      // A query that does not match the current filters came from outside this component, such as
      // browser navigation or a bookmarked URL, so apply it and load the corresponding report.
      this.applySearchFromQuery(nextQuery)
      this.fetchReportFromQuery()
    }
  },
  methods: {
    ...mapActions('payrollReport', ['getPayrollReportWithFilters', 'clearPayrollReport']),
    ...mapActions('payrollReportFilters', ['clearPayrollReportFilters', 'applyPayrollReportFilters']),
    // A URL describes a search, so adopting one fills the inputs and commits them in one go. Deep
    // links and browser navigation therefore land on a loaded report without a Search press.
    applySearchFromQuery (query = this.$route.query) {
      const parsed = parsePayrollReportQuery(query)

      this.payFrequencyId = parsed.payFrequencyId
      this.businessEntityId = parsed.businessEntityId
      this.dateBegin = parsed.dateBegin
      this.dateEnd = parsed.dateEnd
      this.clientId = parsed.clientId
      // null means the query says nothing about the switch, which is not the same as saying off:
      // the store default stands rather than being silently forced.
      if (parsed.showDetails !== null) {
        this.showDetails = parsed.showDetails
      }
      if (parsed.useCustomRange !== null) {
        this.useCustomRange = parsed.useCustomRange
      }

      this.applyPayrollReportFilters(buildAppliedFilters(this.pendingFilters))
    },
    syncReportFiltersToQuery () {
      const nextQuery = buildPayrollReportQuery(this.$route.query, this.reportQueryFilters)

      if (hasQueryChanged(this.$route.query, nextQuery, PAYROLL_REPORT_QUERY_KEYS)) {
        this.$router.replace({ query: nextQuery })
      }
    },
    queryDescribesCurrentFilters (query) {
      return queryDescribesFilters(query, this.reportQueryFilters)
    },
    handleReportFilterChange () {
      if (this.isInitialized) {
        this.syncReportFiltersToQuery()
      }
    },
    fetchReportFromQuery () {
      const { payFrequencyId, dateBegin, dateEnd } = this.appliedFilters

      if (payFrequencyId && dateBegin && dateEnd) {
        this.getPayrollReportWithFilters()
      }
    },
    handleExportToExcel () {
      const allSummaries = []
      if (this.clientPayrollSummariesDataValue.length > 0) {
        for (let client of this.clientPayrollSummariesDataValue) {
          allSummaries.push(...client.payrollSummaries)
        }
      }
      if (this.payrollReport && this.payrollReport.staffPayrollSummaries) {
        allSummaries.push(...this.payrollReport.staffPayrollSummaries)
      }
      const skippedAssignments = this.skippedAssignments
      if (allSummaries.length === 0 && skippedAssignments.length === 0) {
        return
      }

      const types = getPresentTypeSummaries(allSummaries)

      const data = allSummaries.map(summary => {
        const row = {
          payFrequency: getPayFrequencyLabel(this.appliedPayFrequencyId),
          periodPaid: this.appliedPeriod,
          consultant: summary.userFullName,
          client: summary.clientName,
          payRate: formatCurrency(summary.payRate),
          payRateType: getPayRateTypeLabel(summary.payRateTypeId),
          totalPaidHours: summary.totalPaidHours !== null ? parseFloat(summary.totalPaidHours).toFixed(2) : 'N/A',
          totalPay: formatCurrency(summary.totalPay),
          frozenTotal: summary.totalPayEdited === true ? formatCurrency(summary.totalPay) : '',
          computedTotal: summary.totalPayEdited === true ? formatCurrency(summary.computedTotalPay) : '',
          status: ''
        }
        types.forEach(type => {
          const present = isTypePresentOnRow(summary, type.timeTypeId)
          row[`type_${type.timeTypeId}_hours`] = present ? formatDecimal(getTypeSummaryHours(summary, type.timeTypeId)) : 'N/A'
          row[`type_${type.timeTypeId}_amount`] = present ? formatCurrency(getTypeSummaryAmount(summary, type.timeTypeId, TypeSummaryAmountField.PAY)) : 'N/A'
        })
        return row
      })

      const skippedColumnKeys = {
        frequency: 'payFrequency',
        period: 'periodPaid',
        rate: 'payRate',
        rateType: 'payRateType',
        totalHours: 'totalPaidHours',
        total: 'totalPay'
      }
      const skippedSharedValues = {
        frequency: getPayFrequencyLabel(this.appliedPayFrequencyId),
        period: this.appliedPeriod
      }
      skippedAssignments.forEach(skipped => {
        data.push(buildSkippedRow(skipped, types, skippedColumnKeys, skippedSharedValues))
      })

      let headers = ['Pay Frequency', 'Period Paid', 'Client', 'Consultant', 'Standard Rate', 'Pay Rate Type']
      let columns = ['payFrequency', 'periodPaid', 'client', 'consultant', 'payRate', 'payRateType']
      getPayrollTypeColumns(types, this.showDetails).forEach(col => {
        headers.push(col.text)
        columns.push(col.key)
      })
      headers.push('Total Paid Hours', 'Total Pay', 'Frozen Total', 'Computed Total', 'Status')
      columns.push('totalPaidHours', 'totalPay', 'frozenTotal', 'computedTotal', 'status')

      const params = {
        title: headers,
        key: columns,
        data: data,
        autoWidth: true,
        filename: `Payroll`
      }
      excel.exportArray2Excel(params)
    }
  },
  created () {
    this.clearPayrollReportFilters()
    this.clearPayrollReport()
    this.applySearchFromQuery()
    this.isInitialized = true
    this.fetchReportFromQuery()
  }
}
</script>
